import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Loader2, ShieldCheck, Eye, EyeOff, Lock, LogOut } from "lucide-react";

function PwField({ label, value, onChange, testId, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="label-up block mb-1.5">{label}</label>
      <div className="relative">
        <input
          className="brutal-input w-full pr-12"
          type={show ? "text" : "password"}
          required
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          placeholder="••••••••"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
          className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-500 hover:text-[#FF4500] transition-colors"
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );
}

export default function ChangePassword() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const isForced = user?.must_change_password === true;

  const rules = [
    { label: "Al menos 8 caracteres", ok: next.length >= 8 },
    { label: "Al menos una letra", ok: /[a-zA-Z]/.test(next) },
    { label: "Al menos un número", ok: /\d/.test(next) },
    { label: "Distinta a la actual", ok: next.length > 0 && next !== current },
    { label: "Las dos coinciden", ok: next.length > 0 && next === confirm },
  ];
  const allOk = rules.every((r) => r.ok);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allOk) return;
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      await refreshUser();
      toast.success("Contraseña actualizada correctamente");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>
        <div className="brutal-card bg-white p-6 sm:p-8 space-y-5">
          <div>
            <div className="label-up text-[#FF4500] flex items-center gap-1.5">
              <ShieldCheck size={14} /> {isForced ? "Primer acceso" : "Cambiar contraseña"}
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight mt-1">
              {isForced ? "Cambia tu contraseña" : "Nueva contraseña"}
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              {isForced
                ? "Por seguridad, este primer acceso requiere que elijas una contraseña personal. La anterior dejará de funcionar."
                : "Introduce tu contraseña actual y la nueva que quieres usar."}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <PwField
              label="Contraseña actual"
              value={current}
              onChange={setCurrent}
              testId="change-current"
              autoComplete="current-password"
            />
            <PwField
              label="Nueva contraseña"
              value={next}
              onChange={setNext}
              testId="change-new"
              autoComplete="new-password"
            />
            <PwField
              label="Confirmar nueva contraseña"
              value={confirm}
              onChange={setConfirm}
              testId="change-confirm"
              autoComplete="new-password"
            />

            <ul className="text-xs space-y-1 rounded-lg bg-slate-50 border border-slate-200 p-3">
              {rules.map((r, i) => (
                <li
                  key={i}
                  className={r.ok ? "text-emerald-700" : "text-slate-500"}
                  data-testid={`pw-rule-${i}`}
                >
                  {r.ok ? "✓" : "•"} {r.label}
                </li>
              ))}
            </ul>

            <button
              type="submit"
              disabled={!allOk || loading}
              data-testid="change-submit"
              className="brutal-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              Guardar nueva contraseña
            </button>

            {isForced && (
              <button
                type="button"
                onClick={() => { logout(); navigate("/login", { replace: true }); }}
                data-testid="change-logout"
                className="w-full h-10 rounded-lg text-xs font-semibold text-slate-500 hover:text-rose-600 inline-flex items-center justify-center gap-2"
              >
                <LogOut size={14} /> Cerrar sesión
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
